/**
 * Orion-styled wrappers over Cloudscape primitives.
 *
 * Usage: Replace `import { Button } from '@cloudscape-design/components'`
 *   with `import { Button } from 'src/common/UIComponents'`
 */
import React, { useState } from 'react';
import * as Cloudscape from '@cloudscape-design/components';
import {
  Button as OrionButton,
  Stack,
  Alert as OrionAlert,
  Modal as OrionModal,
  Link as OrionLink,
  Text,
  Spinner as OrionSpinner,
  Select as OrionSelect,
  TreeSelect as OrionTreeSelect,
  BadgeGroup,
  Breadcrumbs as OrionBreadcrumbs,
  Pagination as OrionPagination,
  TextField as OrionTextField,
  Switch as OrionSwitch,
  Checkbox as OrionCheckbox,
  Popover as OrionPopover,
  Card as OrionCard,
  DatePicker as OrionDatePicker,
  ToggleButton,
  ToggleButtonGroup,
  DialogTrigger,
} from '@amzn/orion-components';

/* ─── Button ─── */
export const Button: React.FC<any> = (props) => {
  const { variant, onClick, disabled, iconName, children, loading, loadingText, href, target } =
    props;
  const getOrionVariant = () => {
    if (variant === 'primary') return 'solid';
    if (variant === 'link' || variant === 'inline-icon') return 'ghost';
    return 'outlined';
  };
  const getColorScheme = () => {
    if (variant === 'primary') return 'primary';
    return 'secondary';
  };
  const handlePress = () => {
    if (href) {
      window.open(href, target || '_blank', 'noopener,noreferrer');
    } else if (onClick) {
      onClick();
    }
  };
  return (
    <OrionButton
      variant={getOrionVariant()}
      colorScheme={getColorScheme()}
      size="sm"
      onPress={handlePress}
      isDisabled={disabled || loading}
      style={{ minHeight: '32px' }}
    >
      {loading ? (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}
        >
          <span
            style={{
              width: '12px',
              height: '12px',
              border: '2px solid currentColor',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.6s linear infinite',
              display: 'inline-block',
              flexShrink: 0,
            }}
          />
          {loadingText || children}
        </span>
      ) : (
        children
      )}
    </OrionButton>
  );
};

/* ─── SpaceBetween ─── */
export const SpaceBetween: React.FC<any> = (props) => {
  const { direction = 'vertical', size, children } = props;
  return (
    <Stack
      direction={direction === 'horizontal' ? 'horizontal' : 'vertical'}
      gap={size === 'xs' ? 'xs' : size === 'l' ? 'lg' : 'sm'}
    >
      {children}
    </Stack>
  );
};

/* ─── Box ─── */
export const Box: React.FC<any> = (props) => {
  const { float, children } = props;
  return <div style={float ? { float: float as any } : undefined}>{children}</div>;
};

/* ─── Alert ─── */
export const Alert: React.FC<any> = (props) => {
  const { type = 'info', children, header, statusIconAriaLabel, dismissible, onDismiss } = props;

  const alertStyles: Record<
    string,
    { border: string; background: string; iconColor: string; icon: string }
  > = {
    info: { border: '#546B41', background: '#F4F6F2', iconColor: '#546B41', icon: 'ℹ' },
    warning: { border: '#B8860B', background: '#FFF8E1', iconColor: '#B8860B', icon: '⚠' },
    error: { border: '#D32F2F', background: '#FDE8E8', iconColor: '#D32F2F', icon: '✕' },
    success: { border: '#2E7D32', background: '#E8F5E9', iconColor: '#2E7D32', icon: '✓' },
  };

  const style = alertStyles[type] || alertStyles.info;

  return (
    <div
      style={{
        padding: '12px 16px',
        borderRadius: '8px',
        border: `1px solid ${style.border}`,
        backgroundColor: style.background,
        color: '#374151',
        fontSize: '14px',
        lineHeight: '1.5',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-start',
      }}
    >
      <span style={{ color: style.iconColor, fontWeight: 600, flexShrink: 0 }}>{style.icon}</span>
      <div style={{ flex: 1 }}>
        {header && (
          <strong style={{ display: 'block', marginBottom: '2px', color: '#374151' }}>
            {header}
          </strong>
        )}
        {children}
      </div>
      {dismissible && (
        <button
          onClick={onDismiss}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: '#6b7280',
            fontSize: '16px',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

/* ─── Modal ─── */
export const Modal: React.FC<any> = (props) => {
  const { visible, onDismiss, header, footer, size, children } = props;
  if (!visible) return null;
  const titleText = typeof header === 'string' ? header : header?.props?.children || 'Dialog';
  // OrionModal exposes a fixed set of size tokens. We always use 'lg' here:
  // when the caller passes size="max" we apply the `chub-modal-max` CSS
  // class instead (see index.scss) which widens the modal to nearly the
  // full viewport with margins — closer to Cloudscape's `size="max"`
  // behavior than OrionModal's built-in 'fullscreen' (edge-to-edge).
  const modalSize = 'lg' as const;
  return (
    <OrionModal
      isOpen
      onClose={onDismiss || (() => {})}
      title={titleText}
      size={modalSize}
      isDismissable
      footer={footer || undefined}
      className={size === 'max' ? 'chub-modal-max' : undefined}
    >
      {children}
    </OrionModal>
  );
};

/* ─── Link ─── */
export const Link: React.FC<any> = (props) => {
  const { href, external, children, onFollow, onClick } = props;
  const handleClick = (e: any) => {
    if (onFollow) {
      e.preventDefault();
      onFollow(e);
    }
    if (onClick) onClick(e);
  };
  return (
    <a
      href={href || '#'}
      target={external ? '_blank' : undefined}
      onClick={handleClick}
      style={{
        color: 'var(--color-interactive-primary-default, #0891B2)',
        textDecoration: 'none',
        cursor: 'pointer',
      }}
    >
      {children}
    </a>
  );
};

/* ─── Header ─── */
export const Header: React.FC<any> = (props) => {
  const { children, actions, counter, description } = props;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
      }}
    >
      <div>
        <Text as="h2" fontSize="lg" fontWeight="semibold">
          {children}{' '}
          {counter && <span style={{ color: '#6b7280', fontWeight: 400 }}>{counter}</span>}
        </Text>
        {description && (
          <Text fontSize="xs" variant="secondary">
            {description}
          </Text>
        )}
      </div>
      {actions && <div>{actions}</div>}
    </div>
  );
};

/* ─── Spinner ─── */
export const Spinner: React.FC<any> = (props) => {
  const size = props.size === 'large' ? 'lg' : props.size === 'normal' ? 'md' : 'sm';
  return <OrionSpinner size={size} />;
};

/* ─── Tabs ─── */
export const Tabs: React.FC<any> = (props) => {
  const { tabs = [], activeTabId, onChange } = props;
  const [active, setActive] = useState(activeTabId || tabs[0]?.id);
  const handleChange = (id: string) => {
    setActive(id);
    onChange?.({ detail: { activeTabId: id } });
  };
  return (
    <div>
      <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb' }}>
        {tabs.map((t: any) => (
          <button
            key={t.id}
            onClick={() => handleChange(t.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontWeight: active === t.id ? 600 : 400,
              fontSize: '16px',
              color: active === t.id ? '#0891B2' : '#6b7280',
              borderBottom: active === t.id ? '2px solid #0891B2' : '2px solid transparent',
              marginBottom: '-2px',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ paddingTop: '16px' }}>{tabs.find((t: any) => t.id === active)?.content}</div>
    </div>
  );
};

/* ─── ButtonDropdown ─── */
export const ButtonDropdown: React.FC<any> = (props) => {
  const { items = [], onItemClick, children } = props;
  const [open, setOpen] = useState(false);

  const renderItem = (item: any, keyPrefix = '') => (
    <button
      key={`${keyPrefix}${item.id}`}
      onClick={() => {
        setOpen(false);
        onItemClick?.({ detail: item });
      }}
      disabled={item.disabled}
      style={{
        display: 'block',
        width: '100%',
        padding: '8px 16px',
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
        cursor: item.disabled ? 'not-allowed' : 'pointer',
        fontSize: '13px',
        color: item.disabled ? '#9ca3af' : '#374151',
      }}
    >
      {item.text}
    </button>
  );

  const renderItems = (itemList: any[]) =>
    itemList.map((item: any) => {
      // Group item (has nested items array, no id)
      if (item.items && !item.id) {
        return (
          <div
            key={`group-${item.text}`}
            role="group"
            aria-label={item.text}
            data-testid={`group-header-${item.text}`}
          >
            <div
              style={{
                borderTop: '1px solid #e5e7eb',
                marginTop: '4px',
                paddingTop: '4px',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  padding: '6px 16px 2px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em',
                }}
              >
                {item.text}
              </span>
            </div>
            {item.items.map((subItem: any) => renderItem(subItem, `${item.text}-`))}
          </div>
        );
      }
      return renderItem(item);
    });

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <OrionButton
        variant="outlined"
        colorScheme="secondary"
        size="sm"
        onPress={() => setOpen(!open)}
      >
        {children || 'Actions'} ▾
      </OrionButton>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 1000,
            marginTop: '4px',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            minWidth: '160px',
            padding: '4px 0',
          }}
        >
          {renderItems(items)}
        </div>
      )}
    </div>
  );
};

/* ─── ExpandableSection ─── */
export const ExpandableSection: React.FC<any> = (props) => {
  const { headerText, defaultExpanded = true, children } = props;
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          padding: '10px 0',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: 600,
          color: '#1a1a1a',
          fontFamily: 'inherit',
        }}
      >
        <span
          style={{
            transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
            transition: 'transform 0.2s',
          }}
        >
          ▶
        </span>
        {headerText}
      </button>
      {expanded && <div>{children}</div>}
    </div>
  );
};

/* ─── Select ─── */
export const Select: React.FC<any> = (props) => {
  const { selectedOption, onChange, options = [], placeholder, disabled, filteringType } = props;
  if (options.length === 0) return <Cloudscape.Select {...props} />;
  const selectOptions = options.map((o: any) => ({
    value: o.value || '',
    label: o.label || o.value || '',
    description: o.description,
  }));
  return (
    <div style={{ width: '100%' }}>
      <OrionSelect
        options={selectOptions}
        selectedKey={selectedOption?.value ?? null}
        onSelectionChange={(key: any) => {
          const opt = options.find((o: any) => o.value === key);
          onChange?.({ detail: { selectedOption: opt || { value: key, label: key } } });
        }}
        placeholder={placeholder}
        isDisabled={disabled}
        size="sm"
      />
    </div>
  );
};

/* ─── Multiselect ─── */
export const Multiselect: React.FC<any> = (props) => {
  const {
    selectedOptions = [],
    onChange,
    options = [],
    placeholder,
    disabled,
    tokenLimit,
    keepOpen,
  } = props;
  const treeData = options.map((o: any) => ({
    value: o.value || '',
    label: o.label || o.value || '',
  }));
  const selectedValues = selectedOptions.map((o: any) => o.value).filter(Boolean);
  return (
    <div style={{ width: '100%' }}>
      <OrionTreeSelect
        treeData={treeData}
        selectionMode="multiple"
        value={selectedValues}
        onChange={(values: string[]) => {
          const selectedOpts = values
            .map((v) => options.find((o: any) => o.value === v))
            .filter(Boolean);
          onChange?.({ detail: { selectedOptions: selectedOpts } });
        }}
        placeholder={placeholder}
        isDisabled={disabled}
        treeCheckable
        showSearch
        maxTagCount={tokenLimit || 3}
        size="sm"
        defaultOpen={false}
      />
    </div>
  );
};

export const TreeSelect = OrionTreeSelect;

export const Container: React.FC<any> = (props) => {
  const { header, children } = props;
  return (
    <OrionCard elevation="flat" border="solid" padding="md">
      {header && <div style={{ marginBottom: '12px' }}>{header}</div>}
      {children}
    </OrionCard>
  );
};
export const Grid = Cloudscape.Grid;
export const ColumnLayout = Cloudscape.ColumnLayout;
export const FormField: React.FC<any> = (props) => {
  const { label, description, children, stretch } = props;
  return (
    <div style={{ width: stretch ? '100%' : 'auto', marginBottom: '8px' }}>
      {label && (
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
          {label}
        </label>
      )}
      {description && (
        <span style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
          {description}
        </span>
      )}
      {children}
    </div>
  );
};
export const Input: React.FC<any> = (props) => {
  const { value, onChange, placeholder, disabled, type } = props;
  return (
    <OrionTextField
      value={value || ''}
      onChange={(val: any) => onChange?.({ detail: { value: val } })}
      placeholder={placeholder}
      isDisabled={disabled}
      size="sm"
    />
  );
};
export const Flashbar = Cloudscape.Flashbar;
export const StatusIndicator = Cloudscape.StatusIndicator;
export const Popover: React.FC<any> = (props) => {
  const { content, children, position, triggerType } = props;
  return (
    <DialogTrigger>
      {children}
      <OrionPopover
        placement={position === 'right' ? 'right' : position === 'left' ? 'left' : 'bottom'}
      >
        {content}
      </OrionPopover>
    </DialogTrigger>
  );
};
export const TextFilter: React.FC<any> = (props) => {
  const { filteringText, onChange, filteringPlaceholder, countText } = props;
  return (
    <div style={{ width: '100%', maxWidth: '300px' }}>
      <OrionTextField
        value={filteringText || ''}
        onChange={(val: any) => onChange?.({ detail: { filteringText: val } })}
        placeholder={filteringPlaceholder || 'Search...'}
        size="sm"
      />
      {countText && (
        <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
          {countText}
        </span>
      )}
    </div>
  );
};

export const Pagination: React.FC<any> = (props) => {
  const { currentPageIndex, pagesCount, onChange } = props;
  return (
    <OrionPagination
      page={currentPageIndex || 1}
      totalPages={pagesCount || 1}
      onPageChange={(page: number) => onChange?.({ detail: { currentPageIndex: page } })}
    />
  );
};
/* ─── Table ─── */
export const Table: React.FC<any> = (props) => {
  const {
    items = [],
    columnDefinitions = [],
    header,
    filter,
    pagination,
    empty,
    loading,
    loadingText,
    selectionType,
    selectedItems = [],
    onSelectionChange,
    ...rest
  } = props;

  if (loading)
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <OrionSpinner size="md" />
      </div>
    );
  if (items.length === 0 && empty)
    return (
      <div>
        {header}
        {empty}
      </div>
    );

  return (
    <div>
      {header}
      {filter}
      <div
        style={{
          overflowX: 'auto',
          border: '1px solid var(--color-border-default, #e2e5e5)',
          borderRadius: '8px',
          marginTop: '8px',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--color-surface-3, #e7f5f3)' }}>
              {columnDefinitions.map((col: any) => (
                <th
                  key={col.id}
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontWeight: 600,
                    borderBottom: '1px solid var(--color-border-default, #e2e5e5)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {typeof col.header === 'function' ? col.header() : col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item: any, rowIdx: number) => {
              const isSelected = selectedItems.some(
                (s: any) => s === item || JSON.stringify(s) === JSON.stringify(item)
              );
              return (
                <tr
                  key={rowIdx}
                  style={{
                    borderBottom: '1px solid var(--color-border-default, #e2e5e5)',
                    backgroundColor: isSelected ? 'var(--color-surface-3, #e7f5f3)' : 'transparent',
                    cursor: selectionType ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (selectionType && onSelectionChange) {
                      onSelectionChange({
                        detail: {
                          selectedItems:
                            selectionType === 'single'
                              ? [item]
                              : isSelected
                                ? selectedItems.filter((s: any) => s !== item)
                                : [...selectedItems, item],
                        },
                      });
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.backgroundColor =
                        'var(--color-background-hover, #f0f2f2)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {columnDefinitions.map((col: any) => (
                    <td key={col.id} style={{ padding: '10px 12px' }}>
                      {col.cell ? col.cell(item) : item[col.id]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pagination && <div style={{ marginTop: '8px' }}>{pagination}</div>}
    </div>
  );
};
export const Toggle: React.FC<any> = (props) => {
  const { checked, onChange, children } = props;
  return (
    <OrionSwitch
      isSelected={!!checked}
      onChange={(val: any) => onChange?.({ detail: { checked: val } })}
    >
      {children}
    </OrionSwitch>
  );
};
export const DatePicker: React.FC<any> = (props) => {
  const { value, onChange, placeholder, disabled } = props;
  // Convert string "YYYY-MM-DD" to/from Orion's CalendarDate
  const parseDate = (str: string) => {
    if (!str) return undefined;
    const [y, m, d] = str.split('-').map(Number);
    if (!y || !m || !d) return undefined;
    const { CalendarDate } = require('@internationalized/date');
    return new CalendarDate(y, m, d);
  };
  const formatDate = (date: any) => {
    if (!date) return '';
    return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
  };
  return (
    <OrionDatePicker
      value={parseDate(value)}
      onChange={(date: any) => onChange?.({ detail: { value: formatDate(date) } })}
      isDisabled={disabled}
      size="sm"
    />
  );
};
export const Autosuggest = Cloudscape.Autosuggest;
export const Cards = Cloudscape.Cards;
export const Icon = Cloudscape.Icon;
export const Badge = (Cloudscape as any).Badge;
export const BreadcrumbGroup: React.FC<any> = (props) => {
  const { items = [], onFollow, onClick } = props;
  const handler = onFollow || onClick;
  // Map items WITHOUT `href`. Passing href makes OrionBreadcrumbs render a
  // native `<a href>` which performs a real browser navigation. We want SPA
  // routing only, so we omit href and rely entirely on onAction → handler →
  // navigate(). The id is set to the original href value so the handler can
  // reconstruct the navigation target.
  const orionItems = items.map((item: any, idx: number) => ({
    id: item.href || String(idx),
    label: item.text || '',
  }));
  return (
    <OrionBreadcrumbs
      items={orionItems}
      onAction={(key: string) => {
        // OrionBreadcrumbs.onAction passes the item id; synthesize a
        // Cloudscape-like event so callers (e.g. the Breadcrumbs wrapper)
        // can use the same `(e) => navigate(e.detail.href)` pattern they
        // use for Cloudscape.
        handler?.({ detail: { href: key }, preventDefault: () => {} });
      }}
    />
  );
};
export const Checkbox: React.FC<any> = (props) => {
  const { checked, onChange, children, disabled } = props;
  return (
    <OrionCheckbox
      isSelected={!!checked}
      onChange={(val: any) => onChange?.({ detail: { checked: val } })}
      isDisabled={disabled}
    >
      {children}
    </OrionCheckbox>
  );
};
export const ContentLayout = (Cloudscape as any).ContentLayout;
export const Form = (Cloudscape as any).Form;
export const HelpPanel = (Cloudscape as any).HelpPanel;
export const PropertyFilter = (Cloudscape as any).PropertyFilter;
export const SegmentedControl: React.FC<any> = (props) => {
  const { selectedId, onChange, options = [] } = props;
  return (
    <ToggleButtonGroup
      selectionMode="single"
      selectedKeys={new Set([selectedId])}
      onSelectionChange={(keys: any) => {
        const id = Array.from(keys)[0];
        onChange?.({ detail: { selectedId: id } });
      }}
      disallowEmptySelection
    >
      {options.map((opt: any) => (
        <ToggleButton key={opt.id} id={opt.id}>
          {opt.text}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
export const TextContent = (Cloudscape as any).TextContent;
export const TokenGroup: React.FC<any> = (props) => {
  const { items = [], onDismiss } = props;
  if (items.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
      {items.map((item: any, idx: number) => (
        <span
          key={idx}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            fontSize: '12px',
            borderRadius: '4px',
            border: '1px solid var(--color-border-default, #e2e5e5)',
            background: 'var(--color-background-secondary, #fbfcfc)',
            color: 'var(--color-text-primary, #000)',
          }}
        >
          {item.label || item.value || ''}
          {onDismiss && (
            <button
              onClick={() => onDismiss({ detail: { itemIndex: idx } })}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '0 2px',
                fontSize: '14px',
                color: '#6b7280',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </span>
      ))}
    </div>
  );
};

// Type re-exports
export type {
  FlashbarProps,
  TableProps,
  TabsProps,
  BreadcrumbGroupProps,
  ContentLayoutProps,
  PropertyFilterProps,
  ButtonDropdownProps,
  MultiselectProps,
  SelectProps,
  DatePickerProps,
  InputProps,
  FormFieldProps,
  TokenGroupProps,
  SegmentedControlProps,
  AutosuggestProps,
  CheckboxProps,
  PopoverProps,
  ToggleProps,
} from '@cloudscape-design/components';
